import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { createPortal } from "react-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { authenticationService } from "@/api/authentication";
import { creatorCpiAPI, type PaymentProfileInput } from "@/api/creatorCpi";
import { taxDeclarationAPI, type TaxDeclaration } from "@/api/taxDeclaration";
import {
	EMPTY_TAX_DETAILS,
	notifyCreatorTaxDeclarationUpdated,
	toDeclarationInput,
	useCountryTaxConfig,
	validateTaxDetails,
	type TaxDetailsErrors,
	type TaxDetailsValues,
} from "@/features/creator-tax";
import {
	creatorOnboardingApi,
	type CreatorOnboardingProgress,
	type CreatorOnboardingStep,
} from "@/api/creatorOnboarding";
import { AppAutoComplete, AppButton, AppSelect, FormField, Icon, LoadingWrapper } from "@/shared/components";
import creatorverseLogoUrl from "@/assets/icons/Creatorverse_Icon_Gradient.png?url";
import { useAuth } from "@/shared/contexts/AuthContext";
import {
	MOBILE_GUTTER,
	MOBILE_SHEET_TOP_MARGIN,
	SAFE_AREA,
	SHEET_RADIUS,
	TAP_TARGET,
	TAP_TARGET_PRIMARY,
	mediaPhone,
} from "@/shared/theme/breakpoints";
import { hideWhileTyping } from "@/shared/theme/keyboard";
import { phoneCountries, type PhoneCountry } from "@/shared/utils/phoneCountries";
import {
	getBankValidationErrors,
	getProfileValidationErrors,
	type BankValidationErrors,
	type BankValidationValues,
	type ProfileValidationErrors,
} from "../utils/validation";
import { tiktokCreatorLinkApi } from "@/api/tiktokCreatorLink";
import { startTiktokBusinessLinkOAuth } from "@/shared/utils/tiktokBusinessOAuth";
import { useAddressAutocomplete } from "../hooks";
import { getBankFieldRequirements } from "../utils/bankRequirements";
import { notifyCreatorPaymentProfileUpdated } from "../utils/events";
import { OnboardingBankDetailsStep } from "./OnboardingBankDetailsStep";
import { OnboardingTaxDetailsStep } from "./OnboardingTaxDetailsStep";
import { pressedDarken, pressedTint } from "@/shared/theme/pressed";

interface CreatorOnboardingWizardProps {
	open: boolean;
	progress: CreatorOnboardingProgress;
	initialStep?: CreatorOnboardingStep;
	onProgress: (progress: CreatorOnboardingProgress) => void;
	onClose: () => void;
	onReopen: () => void;
}

const ASSET = "/assets/creator-onboarding";
const POPUP_Z_INDEX = 2500;
const ONBOARDING_POPUP_CLASS = "creator-onboarding-popup";
type WizardStep = CreatorOnboardingStep | "tax";
const STEPS: Array<{ key: WizardStep; label: string }> = [
	{ key: "socials", label: "Link your socials" },
	{ key: "profile", label: "Basic profile info" },
	{ key: "bank", label: "Bank details" },
	{ key: "tax", label: "Tax details" },
];

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 2400;
	display: grid;
	place-items: center;
	padding: 24px;
	background: rgba(28, 27, 57, 0.42);

	${mediaPhone`
		place-items: end center;
		padding: 0;
	`}
`;

const sheetSlideUp = keyframes`
	from { transform: translateY(100%); }
	to { transform: translateY(0); }
`;

/** Gives small pill buttons a 44px touch area on phones without changing how they look. */
const phoneHitArea = mediaPhone`
	position: relative;

	&::after {
		content: "";
		position: absolute;
		inset: calc((${TAP_TARGET}px - 100%) / -2) -4px;
	}
`;

const OnboardingPopupStyles = createGlobalStyle`
	.${ONBOARDING_POPUP_CLASS}.ant-select-dropdown {
		z-index: ${POPUP_Z_INDEX} !important;
		/* Fixed-width popups (the 300px phone-code list) stay inside narrow screens. */
		max-width: calc(100vw - 32px);
	}

	.${ONBOARDING_POPUP_CLASS}.ant-select-dropdown .ant-select-item {
		font-size: 16px !important;
	}
`;

const Dialog = styled.div`
	display: flex;
	width: min(1060px, calc(100vw - 48px));
	height: min(640px, calc(100vh - 48px));
	min-height: min(640px, calc(100vh - 48px));
	overflow: hidden;
	border-radius: 24px;
	background: #fdfdfd;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.12);
	font-family: var(--font-family-app);

	/* Phone: a bottom sheet with the standard top gap; the form scrolls between header and footer. */
	${mediaPhone`
		width: 100%;
		height: calc(100vh - ${MOBILE_SHEET_TOP_MARGIN}px);
		height: calc(100dvh - ${MOBILE_SHEET_TOP_MARGIN}px - ${SAFE_AREA.top});
		min-height: 0;
		flex-direction: column;
		border-radius: ${SHEET_RADIUS}px ${SHEET_RADIUS}px 0 0;
		animation: ${sheetSlideUp} 240ms cubic-bezier(0.2, 0, 0, 1);

		@media (prefers-reduced-motion: reduce) {
			animation: none;
		}
	`}
`;

const Sidebar = styled.aside`
	position: relative;
	width: 330px;
	flex: 0 0 330px;
	overflow: hidden;
	padding: 40px;
	background: #fff;

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		background: url("${ASSET}/sidebar-background.jpg") center/cover no-repeat;
		opacity: 0.1;
	}

	/* Phones show the steps in the header stepper instead. */
	${mediaPhone`display: none;`}
`;

const SidebarContent = styled.div`
	position: relative;
	z-index: 1;
`;

const SidebarWatermark = styled.div`
	position: absolute;
	top: 50%;
	left: 115px;
	width: 427px;
	height: 318.251px;
	overflow: hidden;
	transform: translateY(-50%);
	opacity: 0.06;
	pointer-events: none;

	img {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 409.408px;
		height: 242.287px;
		max-width: none;
		transform: translate(-50%, -50%);
	}
`;

const SidebarTitle = styled.h1`
	margin: 0 0 16px;
	color: #303030;
	font-size: 24px;
	font-weight: 700;
	line-height: 1.4;
`;

const SidebarCopy = styled.p`
	margin: 0 0 24px;
	color: #707070;
	font-size: 12px;
	line-height: 1.45;
`;

const StepList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const StepRow = styled.button<{ $active: boolean; $complete: boolean; $clickable: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	min-height: 46px;
	padding: 10px 12px;
	border: 1px solid ${({ $active }) => ($active ? "#9fadf1" : "transparent")};
	border-radius: 8px;
	background: rgba(254, 254, 254, 0.78);
	box-shadow: ${({ $active }) => ($active ? "0 4px 6px rgba(21, 89, 165, 0.12)" : "none")};
	color: ${({ $active, $complete }) => ($active || $complete ? "#303030" : "#707070")};
	font-family: inherit;
	font-size: 14px;
	font-weight: ${({ $active }) => ($active ? 600 : 400)};
	text-align: left;
	cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: 2px;
	}

	> span:first-child {
		display: grid;
		place-items: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: ${({ $complete }) => ($complete ? "#66b026" : "#e6e6e6")};
	}
	> span:first-child > div {
		width: 12.667px;
		height: 9.333px;
	}
	> span:nth-child(2) {
		flex: 1;
	}
	> div:last-child {
		width: 8px;
		height: 14px;
		margin: 5px 8px;
	}

	${pressedTint}
`;

const Main = styled.section`
	position: relative;
	display: flex;
	min-width: 0;
	flex: 1;
	flex-direction: column;
	background: #fdfdfd;

	${mediaPhone`min-height: 0;`}
`;

const CloseRow = styled.div`
	display: flex;
	height: 56px;
	flex: 0 0 56px;
	align-items: center;
	justify-content: flex-end;
	padding-right: 24px;

	/* Phone: the header carries the sidebar's branding (texture and watermark) above the stepper. */
	${mediaPhone`
		position: relative;
		overflow: hidden;
		height: auto;
		flex: 0 0 auto;
		align-items: flex-start;
		justify-content: space-between;
		gap: 8px;
		padding: 20px 8px 16px ${MOBILE_GUTTER}px;
		border-bottom: 1px solid #ebeefd;
		/* A light wash of the brand gradient so the strip reads as branded even though it's small. */
		background: linear-gradient(120deg, rgba(75, 201, 241, 0.025) 0%, rgba(68, 96, 239, 0.015) 45%, rgba(247, 37, 134, 0.025) 100%),
			#fff;

		&::before {
			content: "";
			position: absolute;
			inset: 0;
			background: url("${ASSET}/sidebar-background.jpg") center / cover no-repeat;
			opacity: 0.07;
			pointer-events: none;
		}

		> * {
			position: relative;
			z-index: 1;
		}
	`}
`;

/** Faint brand mark behind the phone header, echoing the desktop sidebar watermark. */
const PhoneHeaderWatermark = styled.img`
	display: none;

	${mediaPhone`
		display: block;
		position: absolute;
		top: 50%;
		right: -36px;
		width: 180px;
		height: auto;
		transform: translateY(-50%);
		opacity: 0.06;
		pointer-events: none;
	`}
`;

/** Phone replacement for the sidebar: the step label and a segmented progress bar. */
const PhoneStepper = styled.div`
	display: none;

	${mediaPhone`
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		min-width: 0;
		padding-top: 4px;
	`}
`;

const PhoneStepperTitle = styled.p`
	margin: 0;
	color: #303030;
	font-size: 18px;
	font-weight: 700;
	line-height: 1.3;
`;

/* Step markers mirror the desktop sidebar's: a circle per step that becomes a check when done.
   Completed steps use the same green as the onboarding assistant and the sidebar; pending and text
   colours are chosen for 3:1 (graphics) and 4.5:1 (text) contrast on the header's tinted background. */
const STEP_TEXT = "#4a4a4a";
const STEP_PENDING = "#6b75a8";
const STEP_DONE = "#66b026";

const PhoneStepperLabel = styled.p`
	margin: 0;
	color: ${STEP_TEXT};
	font-size: 12px;
	line-height: 1.45;

	strong {
		color: #303030;
		font-weight: 600;
	}
`;

const PhoneStepSegments = styled.div`
	display: grid;
	grid-template-columns: repeat(${STEPS.length}, minmax(0, 1fr));
`;

const PhoneStepSegment = styled.button<{ $active: boolean; $complete: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	height: ${TAP_TARGET}px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;

	/* Connector from the previous step's marker. */
	&:not(:first-child)::before {
		content: "";
		position: absolute;
		top: 50%;
		right: 50%;
		left: -50%;
		height: 2px;
		margin-top: -1px;
		background: ${STEP_PENDING};
		opacity: 0.45;
	}

	&:disabled {
		cursor: default;
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
		border-radius: 8px;
	}

	-webkit-tap-highlight-color: transparent;
	touch-action: manipulation;

	/* Pressed: the marker (not the whole 44px hit area) darkens; it never changes size. */
	&:active:not(:disabled) > span {
		filter: brightness(0.88);
	}

	> span {
		position: relative;
		z-index: 1;
		display: grid;
		place-items: center;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		background: ${({ $complete }) => ($complete ? STEP_DONE : "#fff")};
		border: 2px solid ${({ $complete }) => ($complete ? STEP_DONE : STEP_PENDING)};
		color: ${({ $active }) => ($active ? "#3a0ca3" : STEP_TEXT)};
		font-size: 12px;
		font-weight: ${({ $active }) => ($active ? 700 : 500)};
		line-height: 1;
		/* The current step gets a brand-purple ring so it doesn't rely on colour alone. */
		box-shadow: ${({ $active }) => ($active ? "0 0 0 2px #fff, 0 0 0 4px #3a0ca3" : "none")};
	}
`;

const CloseButton = styled.button`
	display: grid;
	place-items: center;
	width: 56px;
	height: 56px;
	padding: 16px;
	border: 0;
	border-radius: 50%;
	background: transparent;
	cursor: pointer;

	${mediaPhone`
		flex: 0 0 ${TAP_TARGET}px;
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
		padding: 15px;
	`}

	${pressedDarken}
`;

const Content = styled.div`
	min-height: 0;
	flex: 1;
	overflow-x: hidden;
	overflow-y: auto;
	margin: 0 24px 0 0;
	padding: 0 8px 40px 40px;
	scrollbar-gutter: stable;
	scrollbar-width: thin;
	scrollbar-color: #bdc6f2 transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		border-radius: 8px;
		background: #bdc6f2;
	}

	${mediaPhone`
		margin: 0;
		padding: 20px ${MOBILE_GUTTER}px 24px;
		overscroll-behavior: contain;
		scrollbar-gutter: auto;
	`}
`;

const Heading = styled.h2`
	margin: 0 0 8px;
	color: #303030;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.3;
`;

const Description = styled.p`
	margin: 0 0 24px;
	color: #707070;
	font-size: 12px;
	line-height: 1.45;
`;

const Footer = styled.footer`
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 72px;
	padding: 16px;
	border-top: 1px solid #ebeefd;
	background: #fff;

	${mediaPhone`
		gap: 12px;
		padding: 12px ${MOBILE_GUTTER}px calc(12px + ${SAFE_AREA.bottom});
	`}

	/* The keyboard would lift the pinned Back / Next over the field being typed in. */
	${hideWhileTyping()}
`;

/** Holds the Back button; empty on the first step. */
const FooterStart = styled.div`
	${mediaPhone`
		display: flex;
		flex: 1;

		&:empty {
			display: none;
		}
	`}
`;

const ActionButton = styled(AppButton)<{ $onboardingOutline?: boolean }>`
	min-width: 128px;
	height: 40px;
	padding: 0 20px;
	font-size: 14px;
	font-weight: 600;

	${mediaPhone`
		flex: 1;
		min-width: 0;
		height: ${TAP_TARGET_PRIMARY}px;
	`}

	&:disabled {
		cursor: not-allowed;
		background: ${({ $onboardingOutline }) => ($onboardingOutline ? "#fff" : "#e6e6e6")};
		color: #9b9b9b;
		border-color: #e6e6e6;
	}
`;

const SocialList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const SocialCard = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px;
	border: 1px solid #dde2fa;
	border-radius: 8px;
	background: #fff;
`;

const SocialIdentity = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;

	strong {
		display: block;
		color: #303030;
		font-size: 14px;
		line-height: 24px;
	}
	small {
		display: block;
		color: #9b9b9b;
		font-size: 12px;
	}
`;

const LinkButton = styled.button<{ $linked: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	min-width: ${({ $linked }) => ($linked ? "96px" : "80px")};
	height: 24px;
	padding: 0 12px;
	border: ${({ $linked }) => ($linked ? "1px solid #3a0ca3" : "0")};
	border-radius: 64px;
	background: ${({ $linked }) => ($linked ? "#fff" : "#3a0ca3")};
	color: ${({ $linked }) => ($linked ? "#3a0ca3" : "#fff")};
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;

	${phoneHitArea}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	${pressedDarken}
`;

const SectionTitle = styled.h3`
	margin: 0 0 16px;
	color: #303030;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
`;

const ProfileCard = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	margin-bottom: 32px;
	padding: 16px;
	border: 1px solid #dde2fa;
	border-radius: 8px;
	background: #f3f5ff;

	/* Narrow screens: the upload button wraps under the photo copy. */
	${mediaPhone`flex-wrap: wrap;`}
`;

const Avatar = styled.img`
	width: 64px;
	height: 64px;
	flex: 0 0 64px;
	border-radius: 50%;
	object-fit: cover;
`;

const PhotoInfo = styled.div`
	min-width: 0;
	flex: 1;
	${mediaPhone`flex-basis: 160px;`}
	strong {
		display: block;
		margin-bottom: 4px;
		color: #303030;
		font-size: 14px;
	}
	small {
		color: #707070;
		font-size: 12px;
	}
`;

const UploadButton = styled.button`
	height: 24px;
	padding: 0 12px;
	border: 1px solid #3a0ca3;
	border-radius: 64px;
	background: #fff;
	color: #3a0ca3;
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;

	${phoneHitArea}

	${pressedDarken}
`;

const HiddenInput = styled.input`
	display: none;
`;

const Fields = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 24px;

	@media (max-width: 860px) {
		grid-template-columns: 1fr;
	}
`;
const Field = styled(FormField)<{ $wide?: boolean }>`
	min-width: 0;
	grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
`;

const Input = styled.input`
	width: 100%;
	height: 40px;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	outline: none;
	background: #fff;
	color: #171717;
	font: inherit;
	font-size: 16px;

	&:focus {
		border-color: #9fadf1;
	}
`;

const StyledSelect = styled(AppSelect)`
	width: 100%;
	height: 40px;
	.ant-select-selector {
		height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}
	.ant-select-selection-item,
	.ant-select-selection-placeholder {
		font-size: 16px !important;
		line-height: 38px !important;
	}
	.ant-select-selection-search-input {
		font-size: 16px !important;
	}
`;

const PhoneField = styled.div`
	display: flex;
	align-items: center;
	height: 40px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	overflow: hidden;
	background: #fff;

	&:focus-within {
		border-color: #9fadf1;
	}
`;

const PhoneCountrySelect = styled(AppSelect)`
	width: 112px;
	height: 38px;
	flex: 0 0 112px;

	.ant-select-selector {
		height: 38px !important;
		padding: 0 4px 0 10px !important;
		border: 0 !important;
		background: transparent !important;
		box-shadow: none !important;
	}
	.ant-select-selection-item,
	.ant-select-selection-placeholder {
		display: flex;
		align-items: center;
		font-size: 16px !important;
		line-height: 30px !important;
		padding-top: 4px !important;
	}
	.ant-select-arrow {
		inset-inline-end: 5px;
		color: #171717;
		font-size: 14px;
	}
`;

const PhoneInput = styled(Input)`
	height: 38px;
	padding-left: 4px;
	border: 0;
	border-radius: 0;
	font-size: 16px;

	&:focus {
		box-shadow: none;
	}
`;

const CountryCompact = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #737373;
	font-size: 16px;
`;

const CountryOption = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
`;

const CountryName = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const CountryCode = styled.span`
	margin-left: auto;
	color: #737373;
`;

const CountryFlag = styled.img`
	width: 24px;
	height: 24px;
	flex: 0 0 24px;
	border-radius: 50%;
	object-fit: cover;
`;

const CountryEmoji = styled.span`
	width: 24px;
	font-size: 19px;
	text-align: center;
`;

const StyledAutoComplete = styled(AppAutoComplete)`
	width: 100%;
	max-width: 100%;
	height: 40px;

	.ant-select-selector {
		height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}

	.ant-select-selection-placeholder,
	.ant-select-selection-item {
		font-size: 16px !important;
		line-height: 38px !important;
	}

	.ant-select-selection-search-input {
		font-size: 16px !important;
	}
`;

const AddressProvider = styled.div`
	padding: 6px 12px;
	border-top: 1px solid #eee;
	color: #737373;
	font-size: 10px;
	text-align: right;
`;

const FormSection = styled.section`
	margin-top: 40px;
`;

const ContactSection = styled.section`
	margin-top: 40px;
`;

const ConfirmMask = styled.div`
	position: absolute;
	inset: 0;
	z-index: 4;
	display: grid;
	place-items: center;
	background: rgba(0, 0, 0, 0.32);

	${mediaPhone`place-items: end center;`}
`;

const ConfirmDialog = styled.div`
	width: 520px;
	max-width: calc(100% - 48px);
	padding: 32px;
	border-radius: 24px;
	background: #fff;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.12);
	text-align: center;

	/* Phone: a sheet inside the wizard sheet. */
	${mediaPhone`
		width: 100%;
		max-width: 100%;
		padding: 24px ${MOBILE_GUTTER}px calc(24px + ${SAFE_AREA.bottom});
		border-radius: ${SHEET_RADIUS}px ${SHEET_RADIUS}px 0 0;
	`}

	h2 {
		margin: 10px 0 8px;
		color: #303030;
		font-size: 24px;

		${mediaPhone`font-size: 18px;`}
	}
	p {
		margin: 0 0 24px;
		color: #707070;
		font-size: 14px;
		line-height: 1.7;
	}
`;

const BrandBridge = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;

	.brand-box {
		display: grid;
		place-items: center;
		width: 60px;
		height: 58px;
	}
	.creatorverse-mark {
		width: 40px;
		height: 24px;
	}
	.handoff-ellipsis {
		width: 24px;
		height: 6px;
	}
	.tiktok-mark svg path[fill]:not([fill="none"]) {
		fill: #171717;
	}
`;

type ProfileForm = {
	legalFirstName: string;
	legalLastName: string;
	country: string;
	mobileCountryCode: string;
	mobileNumber: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	postcode: string;
	state: string;
};

const fileToDataUrl = (file: File) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});

const renderCountryFlag = (country: PhoneCountry) =>
	country.flagUrl ? <CountryFlag src={country.flagUrl} alt="" /> : <CountryEmoji>{country.flag}</CountryEmoji>;

export const CreatorOnboardingWizard = ({
	open,
	progress,
	initialStep,
	onProgress,
	onClose,
	onReopen,
}: CreatorOnboardingWizardProps) => {
	const { user, fetchUserProfile } = useAuth();
	const [step, setStep] = useState<WizardStep>(progress.currentStep);
	const wasOpenRef = useRef(false);
	const [optimisticCompleted, setOptimisticCompleted] = useState(progress.completedSteps);
	const [saving, setSaving] = useState(false);
	const [linked, setLinked] = useState({ tiktok: false, instagram: false });
	const [tiktokHandle, setTiktokHandle] = useState<string>();
	const [tiktokStatusLoading, setTiktokStatusLoading] = useState(false);
	const [linkingTikTok, setLinkingTikTok] = useState(false);
	const [confirmTikTok, setConfirmTikTok] = useState(false);
	const [photoFile, setPhotoFile] = useState<File>();
	const [photoUrl, setPhotoUrl] = useState(user?.image || `${ASSET}/default-avatar.svg`);
	const [bankSaved, setBankSaved] = useState(false);
	const [bankSaving, setBankSaving] = useState(false);
	const [bankPrefillLoading, setBankPrefillLoading] = useState(true);
	const bankSaveRef = useRef<Promise<boolean> | null>(null);
	const profileSaveRef = useRef<Promise<boolean> | null>(null);
	const bankRevisionRef = useRef(0);
	const [existingBankId, setExistingBankId] = useState<string>();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const confirmDialogRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const previousActiveRef = useRef<HTMLElement | null>(null);
	const closeRef = useRef<() => Promise<void>>(async () => undefined);
	const savingRef = useRef(false);
	const finishingRef = useRef(false);
	const confirmTikTokRef = useRef(false);
	const initialName = (user?.name || "").trim().split(/\s+/);
	const initialMobileCountry =
		phoneCountries.find((country) => country.iso === user?.address?.countryCode) ||
		phoneCountries.find((country) => country.callingCode === user?.mobileCountryCode) ||
		phoneCountries.find((country) => country.iso === "AU");
	const [profile, setProfile] = useState<ProfileForm>({
		legalFirstName: user?.legalFirstName || initialName[0] || "",
		legalLastName: user?.legalLastName || initialName.slice(1).join(" "),
		country: user?.address?.countryCode || "",
		mobileCountryCode: user?.mobileCountryCode || initialMobileCountry?.callingCode || "+61",
		mobileNumber: user?.mobileNumber || "",
		addressLine1: user?.address?.addressLine1 || "",
		addressLine2: user?.address?.addressLine2 || "",
		city: user?.address?.city || "",
		postcode: user?.address?.postalCode || "",
		state: user?.address?.state || "",
	});
	const [profileErrors, setProfileErrors] = useState<ProfileValidationErrors>({});
	const [addressSearch, setAddressSearch] = useState({
		addressLine1: profile.addressLine1,
		addressLine2: profile.addressLine2,
	});
	const addressLine1Lookup = useAddressAutocomplete();
	const addressLine2Lookup = useAddressAutocomplete();
	const [mobileCountryIso, setMobileCountryIso] = useState(initialMobileCountry?.iso || "AU");
	const [bank, setBank] = useState<BankValidationValues>({
		accountName: "",
		accountNumber: "",
		bankName: "",
		bsb: "",
		branchCode: "",
		branchName: "",
		swiftBic: "",
		abaRoutingNumber: "",
	});
	const [bankErrors, setBankErrors] = useState<BankValidationErrors>({});
	const [paymentCountry, setPaymentCountry] = useState(profile.country || "SG");
	const [termsAgreed, setTermsAgreed] = useState(false);
	const [taxRegistrationChoice, setTaxRegistrationChoice] = useState<boolean | null>(null);
	// Ticket-348: the tax declaration is captured on this step; there is no rate field.
	const [tax, setTax] = useState<TaxDetailsValues>(EMPTY_TAX_DETAILS);
	const [taxErrors, setTaxErrors] = useState<TaxDetailsErrors>({});
	const [existingTax, setExistingTax] = useState<TaxDeclaration | null>(null);
	const taxDirty =
		!existingTax ||
		existingTax.countryCode !== paymentCountry ||
		tax.entityType !== (existingTax.entityType === "company" ? "company" : "individual") ||
		tax.isTaxRegistered !== existingTax.isTaxRegistered ||
		!!tax.registrationNumber.trim();
	const taxConfig = useCountryTaxConfig(paymentCountry);
	const taxCountryName = phoneCountries.find((country) => country.iso === paymentCountry)?.name ?? paymentCountry;
	const currencyByCountry: Record<string, string> = {
		SG: "SGD",
		AU: "AUD",
		PH: "PHP",
		US: "USD",
		ID: "IDR",
		VN: "VND",
		MY: "MYR",
		TH: "THB",
	};
	const bankCurrency = currencyByCountry[paymentCountry.toUpperCase()] ?? "";
	useEffect(() => {
		if (open && !wasOpenRef.current) setStep(initialStep ?? progress.currentStep);
		wasOpenRef.current = open;
	}, [initialStep, open, progress.currentStep]);
	useEffect(() => {
		setOptimisticCompleted(progress.completedSteps);
	}, [progress.completedSteps]);
	useEffect(() => {
		if (!open) return;
		const previous = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previous;
		};
	}, [open]);

	useLayoutEffect(() => {
		if (!open) return;
		if (bank.accountName) {
			setBankPrefillLoading(false);
			return;
		}
		let cancelled = false;
		setBankPrefillLoading(true);
		void creatorCpiAPI
			.getPaymentProfile()
			.then((existing) => {
				if (cancelled) return;
				if (!existing) return;
				setExistingBankId(existing.id);
				// A late response must not replace details already entered in this wizard.
				if (bankRevisionRef.current > 0) return;
				setPaymentCountry(existing.country);
				setBankSaved(true);
				if (existing.accountNumberUnavailable) {
					setBankErrors((current) => ({
						...current,
						accountNumber: "Re-enter your account number to update these bank details.",
					}));
				}
				setBank({
					accountName: existing.accountNameMasked || "",
					accountNumber: existing.accountNumber || "",
					bankName: existing.bankName || "",
					bsb: existing.localRouting?.bsb || "",
					branchCode: existing.localRouting?.branchCode || "",
					branchName: existing.localRouting?.branchName || "",
					swiftBic: existing.swiftBic || "",
					abaRoutingNumber: existing.localRouting?.abaRoutingNumber || "",
				});
			})
			.catch(() => undefined)
			.finally(() => {
				if (!cancelled) setBankPrefillLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [bank.accountName, open]);

	useEffect(() => {
		if (!open) return;
		taxDeclarationAPI
			.getMine()
			.then((current) => {
				setExistingTax(current);
				setTaxRegistrationChoice(current?.isTaxRegistered ?? null);
				setTax({
					...EMPTY_TAX_DETAILS,
					countryCode: current?.countryCode || profile.country || "",
					entityType: current?.entityType === "company" ? "company" : current ? "individual" : "",
					legalName: current?.legalName || "",
					isTaxRegistered: current?.isTaxRegistered ?? false,
				});
			})
			.catch(() => undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- prefill once per open; the country default must not reset edits
	}, [open]);

	const completed = optimisticCompleted;
	const bankRequirements = getBankFieldRequirements(paymentCountry);
	const updateProfile = (field: keyof ProfileForm, value: string) => {
		setProfileErrors((current) => ({ ...current, [field]: undefined }));
		setProfile((current) => ({ ...current, [field]: value }));
	};
	const searchAddress = (field: "addressLine1" | "addressLine2", value: string) => {
		const lookup = field === "addressLine1" ? addressLine1Lookup : addressLine2Lookup;
		setAddressSearch((current) => ({ ...current, [field]: value }));
		setProfileErrors((current) => ({ ...current, [field]: undefined }));
		setProfile((current) => ({ ...current, [field]: value }));
		lookup.search(value, profile.country);
	};
	const selectAddress = async (field: "addressLine1" | "addressLine2", placeId: string, displayValue: string) => {
		const lookup = field === "addressLine1" ? addressLine1Lookup : addressLine2Lookup;
		try {
			const address = await lookup.select(placeId);
			const selectedLine = address.addressLine1 || address.formattedAddress;
			setAddressSearch((current) => ({
				...current,
				[field]: displayValue,
			}));
			setProfileErrors((current) => ({
				...current,
				[field]: undefined,
				...(field === "addressLine1" ? { city: undefined, state: undefined, postcode: undefined } : {}),
			}));
			setProfile((current) =>
				field === "addressLine1"
					? {
							...current,
							country: address.countryCode || current.country,
							addressLine1: selectedLine,
							city: address.city,
							state: address.state,
							postcode: address.postalCode,
						}
					: { ...current, addressLine2: selectedLine },
			);
		} catch {
			// Keep the typed value so address entry still works when suggestions are unavailable.
		}
	};
	const updateBank = (field: keyof BankValidationValues, value: string | boolean) => {
		bankRevisionRef.current += 1;
		setBankSaved(false);
		setBankErrors((current) => ({ ...current, [field]: undefined }));
		setBank((current) => ({ ...current, [field]: value }));
	};
	const updateTax = <K extends keyof TaxDetailsValues>(field: K, value: TaxDetailsValues[K]) => {
		setTaxErrors((current) => ({ ...current, [field]: undefined }));
		setTax((current) => ({ ...current, [field]: value }));
	};
	const changePaymentCountry = (country: string) => {
		bankRevisionRef.current += 1;
		setPaymentCountry(country);
		if (existingTax?.countryCode === country) {
			setTax({
				...EMPTY_TAX_DETAILS,
				countryCode: country,
				entityType: existingTax.entityType === "company" ? "company" : "individual",
				legalName: existingTax.legalName || "",
				isTaxRegistered: existingTax.isTaxRegistered,
			});
			setTaxRegistrationChoice(existingTax.isTaxRegistered);
		} else {
			setTax((current) => ({ ...current, countryCode: country, isTaxRegistered: false, registrationNumber: "" }));
			setTaxRegistrationChoice(null);
		}
		setTaxErrors({});
		setBankSaved(false);
	};
	const changeTaxRegistration = (registered: boolean) => {
		setTaxRegistrationChoice(registered);
		setTaxErrors((current) => ({ ...current, registrationChoice: undefined, registrationNumber: undefined }));
		updateTax("isTaxRegistered", registered);
	};
	const withComplete = (key: CreatorOnboardingStep) => [...new Set([...completed, key])];
	const navigateToStep = (target: WizardStep) => {
		if (target === step) return;
		if (contentRef.current) contentRef.current.scrollTop = 0;
		setStep(target);
	};

	const persistStep = async (currentStep: CreatorOnboardingStep, completedSteps = completed) => {
		const next = await creatorOnboardingApi.update({ currentStep, completedSteps });
		onProgress(next);
		return next;
	};

	const close = async () => {
		const previousProgress = progress;
		const optimisticProgress: CreatorOnboardingProgress = {
			...progress,
			currentStep: step === "tax" ? "bank" : step,
			completedSteps: completed,
			updatedAt: new Date().toISOString(),
		};

		setSaving(true);
		onProgress(optimisticProgress);
		onClose();
		try {
			await persistStep(step === "tax" ? "bank" : step);
		} catch {
			onProgress(previousProgress);
			onReopen();
			message.error("Unable to save onboarding progress. Please try again.");
		} finally {
			setSaving(false);
		}
	};
	closeRef.current = close;
	savingRef.current = saving;
	confirmTikTokRef.current = confirmTikTok;

	useEffect(() => {
		if (!open) return;
		previousActiveRef.current = document.activeElement as HTMLElement | null;
		const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
		const focusableSelector =
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

		const onKeyDown = (event: KeyboardEvent) => {
			const activeDialog = confirmDialogRef.current ?? dialogRef.current;
			if (!activeDialog) return;
			if (event.key === "Escape") {
				event.preventDefault();
				event.stopPropagation();
				if (confirmTikTokRef.current) setConfirmTikTok(false);
				else if (!savingRef.current) void closeRef.current();
				return;
			}
			if (event.key !== "Tab") return;
			const focusable = Array.from(activeDialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(
				(element) => element.getClientRects().length > 0,
			);
			if (focusable.length === 0) {
				event.preventDefault();
				activeDialog.focus();
				return;
			}
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && (document.activeElement === first || document.activeElement === activeDialog)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKeyDown, true);
		return () => {
			window.cancelAnimationFrame(focusFrame);
			document.removeEventListener("keydown", onKeyDown, true);
			previousActiveRef.current?.focus();
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		let active = true;
		setTiktokStatusLoading(true);
		tiktokCreatorLinkApi
			.getStatus()
			.then((result) => {
				if (!active) return;
				setLinked((value) => ({ ...value, tiktok: result.linked }));
				setTiktokHandle(result.username || undefined);
			})
			.catch(() => {
				if (!active) return;
				setLinked((value) => ({ ...value, tiktok: false }));
				setTiktokHandle(undefined);
			})
			.finally(() => {
				if (active) setTiktokStatusLoading(false);
			});
		return () => {
			active = false;
		};
	}, [open]);

	useEffect(() => {
		if (!confirmTikTok) return;
		window.requestAnimationFrame(() => confirmDialogRef.current?.focus());
	}, [confirmTikTok]);

	const beginTikTokLink = async () => {
		setLinkingTikTok(true);
		try {
			await startTiktokBusinessLinkOAuth({ reopenStep: "socials" });
		} catch (error: unknown) {
			setLinkingTikTok(false);
			setConfirmTikTok(false);
			const messageText = error instanceof Error ? error.message : undefined;
			message.error(messageText || "Unable to start TikTok linking. Please try again.");
		}
	};

	const disconnectTikTok = async () => {
		setLinkingTikTok(true);
		try {
			await tiktokCreatorLinkApi.unlinkAccount();
			setLinked((value) => ({ ...value, tiktok: false }));
			setTiktokHandle(undefined);
			message.success("TikTok disconnected.");
		} catch (error: unknown) {
			const axiosMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
			message.error(axiosMessage || "Unable to disconnect TikTok.");
		} finally {
			setLinkingTikTok(false);
		}
	};

	const nextSocials = (target: WizardStep = "profile") => {
		// Linking a social account saves its completion separately. Navigation stays local.
		navigateToStep(target);
	};

	// Auto-complete Socials the moment a link actually succeeds, rather than
	// waiting for the creator to click Next again — the checkmark should follow
	// the real action, not a separate confirmation step.
	useEffect(() => {
		if (!open) return;
		const hasLinkedSocial = linked.tiktok || linked.instagram;
		if (!hasLinkedSocial || completed.includes("socials")) return;
		const previousCompleted = completed;
		const nextCompleted = withComplete("socials");
		setOptimisticCompleted(nextCompleted);
		persistStep(step === "tax" ? "bank" : step, nextCompleted).catch(() => {
			// Roll back so the checkmark doesn't lie about what's actually saved —
			// this effect will retry as soon as it re-runs (e.g. reopening the wizard).
			setOptimisticCompleted(previousCompleted);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, linked.tiktok, linked.instagram]);

	const nextProfile = (target: WizardStep = "bank") => {
		const errors = getProfileValidationErrors(profile);
		setProfileErrors(errors);
		if (Object.keys(errors).length > 0) return;
		const previousCompleted = completed;
		const nextCompleted = withComplete("profile");
		setOptimisticCompleted(nextCompleted);
		navigateToStep(target);
		// Queue behind any save still in flight so edits made after it started are sent too.
		const previousSave = profileSaveRef.current;
		let pending: Promise<boolean> | undefined = undefined;
		pending = (async () => {
			if (previousSave) await previousSave;
			try {
				await authenticationService.updateUser({
					name: `${profile.legalFirstName.trim()} ${profile.legalLastName.trim()}`,
					legalFirstName: profile.legalFirstName.trim(),
					legalLastName: profile.legalLastName.trim(),
					mobileCountryCode: profile.mobileCountryCode,
					mobileNumber: profile.mobileNumber.trim(),
					profileImage: photoFile ? await fileToDataUrl(photoFile) : undefined,
					address: {
						countryCode: profile.country,
						addressLine1: profile.addressLine1.trim(),
						addressLine2: profile.addressLine2.trim(),
						city: profile.city.trim(),
						state: profile.state.trim(),
						postalCode: profile.postcode.trim(),
					},
				});
				await fetchUserProfile();
				await persistStep("bank", nextCompleted);
				return true;
			} catch {
				setOptimisticCompleted(previousCompleted);
				message.error("Unable to save your profile information.");
				return false;
			} finally {
				if (profileSaveRef.current === pending) profileSaveRef.current = null;
			}
		})();
		profileSaveRef.current = pending;
	};

	const saveBank = (): Promise<boolean> => {
		const errors = getBankValidationErrors(paymentCountry, bank);
		setBankErrors(errors);
		if (Object.keys(errors).length > 0) return Promise.resolve(false);
		if (bankSaveRef.current) return bankSaveRef.current;
		if (bankSaved) return Promise.resolve(true);
		const revision = bankRevisionRef.current;
		setBankSaving(true);
		const pending = (async () => {
			try {
				const country = paymentCountry;
				const localRouting: Record<string, string> = {};
				if (bankRequirements.bsb) localRouting.bsb = bank.bsb.trim();
				if (bankRequirements.branchCode) localRouting.branchCode = bank.branchCode.trim();
				if (bankRequirements.branchName) localRouting.branchName = bank.branchName.trim();
				if (bankRequirements.abaRoutingNumber) {
					localRouting.abaRoutingNumber = bank.abaRoutingNumber.trim();
				}
				const input: PaymentProfileInput = {
					country,
					bankName: bank.bankName.trim(),
					accountName: bank.accountName.trim(),
					accountNumber: bank.accountNumber.trim(),
					swiftBic: bank.swiftBic.trim(),
					currency: bankCurrency,
					localRouting,
				};
				// The initial lookup may still be pending when the creator reaches Bank.
				// Recheck before adding so this wizard edits an existing profile instead of duplicating it.
				const profileId = existingBankId ?? (await creatorCpiAPI.getPaymentProfile())?.id;
				const saved = profileId
					? await creatorCpiAPI.updatePaymentProfile(profileId, input)
					: await creatorCpiAPI.addPaymentProfile(input);
				setExistingBankId(saved.id);
				if (bankRevisionRef.current === revision) setBankSaved(true);
				notifyCreatorPaymentProfileUpdated();
				return bankRevisionRef.current === revision;
			} catch {
				message.error("Unable to save bank details. Please try again.");
				return false;
			} finally {
				setBankSaving(false);
				bankSaveRef.current = null;
			}
		})();
		bankSaveRef.current = pending;
		return pending;
	};

	const finish = async () => {
		if (finishingRef.current) return;
		// The declaration is (re)recorded when there is none yet or the creator changed it. A registered creator
		// with a number on file may leave the number blank only if nothing else changed (the number is never echoed back).
		const needsDeclaration = !existingTax || taxDirty;
		if (paymentCountry !== "US" && taxRegistrationChoice === null) {
			setTaxErrors({ registrationChoice: "Choose Yes or No." });
			return;
		}
		const taxForSave = {
			...tax,
			countryCode: paymentCountry,
			legalName:
				tax.entityType === "company"
					? existingTax?.entityType === "company" && existingTax.countryCode === paymentCountry
						? existingTax.legalName
						: bank.accountName.trim()
					: `${profile.legalFirstName.trim()} ${profile.legalLastName.trim()}`.trim(),
			isTaxRegistered: paymentCountry === "US" ? true : taxRegistrationChoice === true,
		};
		// Declared for any country; with no configuration only the number format goes unchecked.
		const foundTaxErrors = needsDeclaration ? validateTaxDetails(taxForSave, taxConfig.config) : {};
		setTaxErrors(foundTaxErrors);
		if (Object.keys(foundTaxErrors).length > 0) return;
		finishingRef.current = true;
		setSaving(true);
		try {
			if (profileSaveRef.current && !(await profileSaveRef.current)) {
				setStep("profile");
				return;
			}
			// Declare tax before the final bank save: the backend completes onboarding only once
			// every step is done and a tax declaration exists.
			if (needsDeclaration) {
				const saved = await taxDeclarationAPI.declare(toDeclarationInput(taxForSave, "onboarding"));
				setExistingTax(saved);
				notifyCreatorTaxDeclarationUpdated();
			}
			if (!(await saveBank())) {
				navigateToStep("bank");
				return;
			}
			const nextCompleted = withComplete("bank");
			const onboardingComplete = (["socials", "profile", "bank"] as const).every((key) =>
				nextCompleted.includes(key),
			);
			const next = await creatorOnboardingApi.update({
				currentStep: onboardingComplete ? "bank" : progress.currentStep,
				completedSteps: nextCompleted,
				...(onboardingComplete ? { status: "completed" as const } : {}),
			});
			onProgress(next);
			onClose();
			message.success(
				onboardingComplete
					? "You have completed onboarding. Welcome to Creatorverse!"
					: "Bank details saved successfully.",
			);
		} catch {
			message.error("Unable to finish onboarding. Your saved details are safe; please retry.");
		} finally {
			finishingRef.current = false;
			setSaving(false);
		}
	};

	const nextBank = (target: WizardStep = "tax") => {
		if (bankPrefillLoading) return;
		const errors = getBankValidationErrors(paymentCountry, bank);
		setBankErrors(errors);
		if (Object.keys(errors).length > 0) return;
		// Save now so the details survive leaving the wizard on the Tax step. The backend only
		// completes onboarding once a tax declaration also exists; finish() re-uses this save.
		navigateToStep(target);
		void saveBank().then((saved) => {
			if (!saved) setStep((current) => (current === target ? "bank" : current));
		});
	};

	const navigateFromSidebar = (target: WizardStep) => {
		if (target === step) return;
		const targetIndex = STEPS.findIndex((item) => item.key === target);
		if (targetIndex < stepIndex) {
			navigateToStep(target);
			return;
		}

		if (step === "socials") nextSocials(target);
		else if (step === "profile") nextProfile(target);
		else if (step === "bank") nextBank(target);
	};

	const selectPhoto = (file?: File) => {
		if (!file) return;
		if (!["image/jpeg", "image/png"].includes(file.type) || file.size > 5 * 1024 * 1024) {
			message.error("Choose a JPG or PNG image up to 5MB.");
			return;
		}
		if (photoUrl.startsWith("blob:")) URL.revokeObjectURL(photoUrl);
		setPhotoFile(file);
		setPhotoUrl(URL.createObjectURL(file));
	};

	if (!open) return null;
	const stepIndex = STEPS.findIndex((item) => item.key === step);
	const taxComplete = existingTax?.countryCode === paymentCountry && !taxDirty;
	const isStepComplete = (key: WizardStep) =>
		key === "bank"
			? bankSaved || bankSaving || completed.includes("bank")
			: key === "tax"
				? taxComplete
				: completed.includes(key);

	return createPortal(
		<Overlay>
			<OnboardingPopupStyles />
			<Dialog
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={`creator-onboarding-title-${step}`}
				tabIndex={-1}
			>
				<Sidebar>
					<SidebarWatermark aria-hidden>
						<img src={creatorverseLogoUrl} alt="" />
					</SidebarWatermark>
					<SidebarContent>
						<SidebarTitle>
							Let&apos;s finish your
							<br />
							profile!
						</SidebarTitle>
						<SidebarCopy>
							We just need a few details on your profile
							<br />
							before we can send your fee.
						</SidebarCopy>
						<StepList>
							{STEPS.map((item) => {
								const isActive = item.key === step;
								const isComplete = isStepComplete(item.key);
								const canNavigate = !isActive;

								return (
									<StepRow
										key={item.key}
										type="button"
										$active={isActive}
										$complete={isComplete}
										$clickable={canNavigate}
										disabled={!canNavigate}
										aria-current={isActive ? "step" : undefined}
										onClick={() => navigateFromSidebar(item.key)}
									>
										<span>
											<Icon name="onboardingCheck" size={12.667} color="#fff" />
										</span>
										<span>{item.label}</span>
										{isActive && (
											<Icon src={`${ASSET}/arrow-active.svg`} size={14} color="#f72586" />
										)}
									</StepRow>
								);
							})}
						</StepList>
					</SidebarContent>
				</Sidebar>
				<Main>
					<CloseRow>
						<PhoneHeaderWatermark src={creatorverseLogoUrl} alt="" aria-hidden />
						<PhoneStepper>
							<PhoneStepperTitle>Let&apos;s finish your profile!</PhoneStepperTitle>
							<PhoneStepperLabel>
								Step {stepIndex + 1} of {STEPS.length} · <strong>{STEPS[stepIndex].label}</strong>
							</PhoneStepperLabel>
							<PhoneStepSegments>
								{STEPS.map((item, index) => {
									const isActive = item.key === step;
									const isComplete = isStepComplete(item.key);
									return (
										<PhoneStepSegment
											key={item.key}
											type="button"
											$active={isActive}
											$complete={isComplete}
											disabled={isActive}
											aria-current={isActive ? "step" : undefined}
											aria-label={`Step ${index + 1}: ${item.label}${isComplete ? " (completed)" : ""}`}
											// Same path as the sidebar: validates and saves before moving forward.
											onClick={() => navigateFromSidebar(item.key)}
										>
											<span aria-hidden>
												{isComplete ? (
													<Icon name="onboardingCheck" size={12} color="#fff" />
												) : (
													index + 1
												)}
											</span>
										</PhoneStepSegment>
									);
								})}
							</PhoneStepSegments>
						</PhoneStepper>
						<CloseButton
							ref={closeButtonRef}
							type="button"
							disabled={saving}
							aria-label="Close onboarding"
							onClick={() => void close()}
						>
							<Icon src={`${ASSET}/close.svg`} size={14} color="#454545" />
						</CloseButton>
					</CloseRow>
					<Content ref={contentRef}>
						<div hidden={step !== "socials"}>
							<>
								<Heading id="creator-onboarding-title-socials">Link your socials</Heading>
								<Description>
									Connect the platforms your audience knows you from. Linked accounts help brands
									verify your reach when they review your profile.
								</Description>
								<SocialList>
									{(["tiktok", "instagram"] as const).map((platform) => {
										const isInstagram = platform === "instagram";
										return (
											<SocialCard
												key={platform}
												style={
													isInstagram ? { opacity: 0.45, pointerEvents: "none" } : undefined
												}
											>
												<SocialIdentity>
													<Icon
														src={`${ASSET}/${platform}.svg`}
														size={24}
														preserveOriginalColor
													/>
													<div>
														<strong>
															{platform === "tiktok" ? "TikTok" : "Instagram"}
														</strong>
														<small>
															{isInstagram
																? "Coming soon"
																: tiktokStatusLoading
																	? "Checking…"
																	: linked.tiktok && tiktokHandle
																		? `@${tiktokHandle}`
																		: linked.tiktok
																			? "Linked"
																			: "Not linked"}
														</small>
													</div>
												</SocialIdentity>
												<LinkButton
													type="button"
													$linked={linked[platform]}
													disabled={isInstagram || tiktokStatusLoading || linkingTikTok}
													onClick={() => {
														if (isInstagram) return;
														if (linked.tiktok) void disconnectTikTok();
														else setConfirmTikTok(true);
													}}
												>
													{isInstagram
														? "Link"
														: tiktokStatusLoading
															? "Loading…"
															: linked.tiktok
																? "Disconnect"
																: linkingTikTok
																	? "Linking…"
																	: "Link"}
												</LinkButton>
											</SocialCard>
										);
									})}
								</SocialList>
							</>
						</div>
						<div hidden={step !== "profile"}>
							<>
								<Heading id="creator-onboarding-title-profile">Basic profile information</Heading>
								<FormSection style={{ marginTop: 24 }}>
									<SectionTitle>Profile</SectionTitle>
									<ProfileCard>
										<Avatar src={photoUrl} alt="Profile preview" />
										<PhotoInfo>
											<strong>Profile Photo</strong>
											<small>JPG or PNG. 5MB Limit. Square photo look best.</small>
										</PhotoInfo>
										<UploadButton type="button" onClick={() => fileInputRef.current?.click()}>
											Upload Photo
										</UploadButton>
										<HiddenInput
											ref={fileInputRef}
											type="file"
											accept="image/jpeg,image/png"
											onChange={(event) => selectPhoto(event.target.files?.[0])}
										/>
									</ProfileCard>
									<Fields>
										<Field
											htmlFor="creator-onboarding-first-name"
											label="First Name (Legal)"
											error={profileErrors.legalFirstName}
											required
										>
											<Input
												id="creator-onboarding-first-name"
												value={profile.legalFirstName}
												onChange={(event) =>
													updateProfile("legalFirstName", event.target.value)
												}
											/>
										</Field>
										<Field
											htmlFor="creator-onboarding-last-name"
											label="Last Name (Legal)"
											error={profileErrors.legalLastName}
											required
										>
											<Input
												id="creator-onboarding-last-name"
												value={profile.legalLastName}
												onChange={(event) => updateProfile("legalLastName", event.target.value)}
											/>
										</Field>
										<Field
											htmlFor="creator-onboarding-country"
											label="Your country of residence"
											error={profileErrors.country}
											required
										>
											<StyledSelect
												id="creator-onboarding-country"
												showSearch
												optionFilterProp="label"
												getPopupContainer={() => document.body}
												popupClassName={ONBOARDING_POPUP_CLASS}
												value={profile.country || undefined}
												onChange={(value) => {
													const selectedCountry = phoneCountries.find(
														(country) => country.iso === value,
													);
													updateProfile("country", value);
													if (selectedCountry) setMobileCountryIso(selectedCountry.iso);
													setAddressSearch({ addressLine1: "", addressLine2: "" });
													setProfile((current) => ({
														...current,
														country: value,
														mobileCountryCode:
															selectedCountry?.callingCode || current.mobileCountryCode,
														addressLine1: "",
														addressLine2: "",
														city: "",
														state: "",
														postcode: "",
													}));
													addressLine1Lookup.clear();
													addressLine2Lookup.clear();
												}}
												options={phoneCountries.map((country) => ({
													value: country.iso,
													label: country.name,
												}))}
											/>
										</Field>
									</Fields>
									<ContactSection>
										<SectionTitle>Contact &amp; Address</SectionTitle>
										<Fields>
											<Field
												htmlFor="creator-onboarding-mobile"
												label="Mobile"
												error={profileErrors.mobileNumber}
												required
											>
												<PhoneField>
													<PhoneCountrySelect
														aria-label="Phone country"
														value={mobileCountryIso}
														popupMatchSelectWidth={300}
														getPopupContainer={() => document.body}
														popupClassName={ONBOARDING_POPUP_CLASS}
														onChange={(iso) => {
															const country = phoneCountries.find(
																(option) => option.iso === iso,
															);
															if (!country) return;
															setMobileCountryIso(country.iso);
															updateProfile("mobileCountryCode", country.callingCode);
														}}
														options={phoneCountries.map((country) => ({
															value: country.iso,
															title: `${country.name} ${country.callingCode}`,
															label: (
																<CountryCompact>
																	{renderCountryFlag(country)}
																	{country.callingCode}
																</CountryCompact>
															),
															country,
														}))}
														optionRender={(option) => {
															const country = option.data.country as PhoneCountry;
															return (
																<CountryOption>
																	{renderCountryFlag(country)}
																	<CountryName>{country.name}</CountryName>
																	<CountryCode>{country.callingCode}</CountryCode>
																</CountryOption>
															);
														}}
													/>
													<PhoneInput
														id="creator-onboarding-mobile"
														value={profile.mobileNumber}
														onChange={(event) =>
															updateProfile("mobileNumber", event.target.value)
														}
													/>
												</PhoneField>
											</Field>
											<Field
												$wide
												htmlFor="creator-onboarding-address-line-1"
												label="Address Line 1"
												error={profileErrors.addressLine1}
												required
											>
												<StyledAutoComplete
													id="creator-onboarding-address-line-1"
													value={addressSearch.addressLine1}
													options={addressLine1Lookup.suggestions.map((suggestion) => ({
														value: suggestion.label,
														label: suggestion.label,
														placeId: suggestion.placeId,
													}))}
													filterOption={false}
													getPopupContainer={() => document.body}
													popupClassName={ONBOARDING_POPUP_CLASS}
													placeholder={
														profile.country
															? "Start typing your address"
															: "Select a country first"
													}
													disabled={!profile.country}
													notFoundContent={addressLine1Lookup.loading ? "Searching..." : null}
													popupRender={(menu) => (
														<>
															{menu}
															<AddressProvider>Powered by Google</AddressProvider>
														</>
													)}
													onChange={(value) => searchAddress("addressLine1", String(value))}
													onSelect={(value, option) =>
														void selectAddress(
															"addressLine1",
															String((option as { placeId: string }).placeId),
															String(value),
														)
													}
												/>
											</Field>
											<Field
												$wide
												htmlFor="creator-onboarding-address-line-2"
												label="Address Line 2 (optional)"
												error={profileErrors.addressLine2}
											>
												<StyledAutoComplete
													id="creator-onboarding-address-line-2"
													value={addressSearch.addressLine2}
													options={addressLine2Lookup.suggestions.map((suggestion) => ({
														value: suggestion.label,
														label: suggestion.label,
														placeId: suggestion.placeId,
													}))}
													filterOption={false}
													getPopupContainer={() => document.body}
													popupClassName={ONBOARDING_POPUP_CLASS}
													placeholder={
														profile.country
															? "Start typing your address"
															: "Select a country first"
													}
													disabled={!profile.country}
													notFoundContent={addressLine2Lookup.loading ? "Searching..." : null}
													popupRender={(menu) => (
														<>
															{menu}
															<AddressProvider>Powered by Google</AddressProvider>
														</>
													)}
													onChange={(value) => searchAddress("addressLine2", String(value))}
													onSelect={(value, option) =>
														void selectAddress(
															"addressLine2",
															String((option as { placeId: string }).placeId),
															String(value),
														)
													}
												/>
											</Field>
											<Field
												htmlFor="creator-onboarding-city"
												label="Suburb / City"
												error={profileErrors.city}
												required
											>
												<Input
													id="creator-onboarding-city"
													value={profile.city}
													onChange={(event) => updateProfile("city", event.target.value)}
												/>
											</Field>
											<Field
												htmlFor="creator-onboarding-state"
												label="State / Territory"
												error={profileErrors.state}
												required
											>
												<Input
													id="creator-onboarding-state"
													value={profile.state}
													onChange={(event) => updateProfile("state", event.target.value)}
												/>
											</Field>
											<Field
												htmlFor="creator-onboarding-postcode"
												label="Postcode"
												error={profileErrors.postcode}
												required
											>
												<Input
													id="creator-onboarding-postcode"
													value={profile.postcode}
													onChange={(event) => updateProfile("postcode", event.target.value)}
												/>
											</Field>
										</Fields>
									</ContactSection>
								</FormSection>
							</>
						</div>
						<div hidden={step !== "bank"}>
							<LoadingWrapper
								isLoading={bankPrefillLoading}
								label="Loading saved bank details"
								indicatorSize={64}
								stickyIndicator
							>
								<OnboardingBankDetailsStep
									active={step === "bank"}
									country={paymentCountry}
									bankCurrency={bankCurrency}
									bank={bank}
									errors={bankErrors}
									requirements={bankRequirements}
									onCountryChange={changePaymentCountry}
									onBankChange={updateBank}
								/>
							</LoadingWrapper>
						</div>
						<div hidden={step !== "tax"}>
							<OnboardingTaxDetailsStep
								country={paymentCountry}
								countryName={taxCountryName}
								tax={tax}
								errors={taxErrors}
								config={taxConfig.config}
								existingNumberLast4={
									existingTax?.countryCode === paymentCountry
										? existingTax.registrationNumberLast4
										: null
								}
								taxDirty={taxDirty}
								taxRegistrationChoice={taxRegistrationChoice}
								agreed={termsAgreed}
								onRegistrationChange={changeTaxRegistration}
								onTaxChange={updateTax}
								onAgreementChange={setTermsAgreed}
							/>
						</div>
					</Content>
					<Footer>
						<FooterStart>
							{stepIndex > 0 && (
								<ActionButton
									type="button"
									variant="outline"
									$onboardingOutline
									onClick={() => navigateToStep(STEPS[stepIndex - 1].key)}
								>
									Back
								</ActionButton>
							)}
						</FooterStart>
						<ActionButton
							type="button"
							variant="primary"
							aria-busy={step === "tax" && (saving || bankSaving)}
							disabled={
								(step === "tax" && (saving || bankSaving || !termsAgreed)) ||
								(step === "bank" && bankPrefillLoading)
							}
							onClick={() =>
								void (step === "socials"
									? nextSocials()
									: step === "profile"
										? nextProfile()
										: step === "bank"
											? nextBank()
											: finish())
							}
						>
							{step === "tax" && (saving || bankSaving) ? (
								<>
									<LoadingOutlined spin aria-hidden="true" /> Finishing...
								</>
							) : step === "tax" ? (
								"Agree & Finish"
							) : (
								"Next"
							)}
						</ActionButton>
					</Footer>
					{confirmTikTok && (
						<ConfirmMask>
							<ConfirmDialog
								ref={confirmDialogRef}
								role="dialog"
								aria-modal="true"
								aria-labelledby="creator-onboarding-tiktok-title"
								tabIndex={-1}
							>
								<BrandBridge>
									<span className="brand-box">
										<img
											className="creatorverse-mark"
											src={creatorverseLogoUrl}
											alt="Creatorverse"
										/>
									</span>
									<Icon
										className="handoff-ellipsis"
										src={`${ASSET}/handoff-ellipsis.svg`}
										size={24}
										preserveOriginalColor
									/>
									<span className="brand-box">
										<Icon
											className="tiktok-mark"
											src={`${ASSET}/tiktok.svg`}
											size={31}
											alt="TikTok"
											preserveOriginalColor
										/>
									</span>
								</BrandBridge>
								<h2 id="creator-onboarding-tiktok-title">Continue to TikTok</h2>
								<p>
									Creatorverse will open TikTok in a secure window so you can sign in and choose what
									to share. We never see your TikTok password.
								</p>
								<ActionButton
									type="button"
									variant="primary"
									disabled={linkingTikTok}
									onClick={() => void beginTikTokLink()}
								>
									{linkingTikTok ? "Opening TikTok…" : "Continue to TikTok"}
								</ActionButton>
								<p style={{ marginTop: 20, marginBottom: 0, fontSize: 12 }}>
									You can revoke access at any time from your TikTok settings.
								</p>
							</ConfirmDialog>
						</ConfirmMask>
					)}
				</Main>
			</Dialog>
		</Overlay>,
		document.body,
	);
};
